/* eslint-disable react/jsx-no-undef */
/* eslint-disable react/react-in-jsx-scope */
import { LocalDate } from '@js-joda/core';
import { isEmpty, noop } from 'lodash';
import { useMemo } from 'react';

import { ChronicDisease, ChronicDiseaseQuery } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { dateFormatter, Ordering } from '@dreryk/react-common';
import {
   colorsMatrix,
   createDynamicColumnsWidth,
   JustifyContent,
   LinkSize,
   StyledFlex,
   TableColumnBackgroundColorResolver,
   TableColumnConfiguration,
   Text,
   TextPlaceholder,
   ValueRendererProps,
} from '@dreryk/react-components';

import { ConfidentialMask, getConfidentialAccessButton } from '~libs.desktop.shared.medical-data-confidentiality';
import { PractitionerSafePresenter } from '~libs.desktop.shared.practitioner-safe-presenter';
import { PatientMedicalInformationRemoveModalOpener } from '~libs.desktop.patient-medical-information';

import { AttachmentsLinks } from '../../shared/AttachmentLinks/AttachmentsLinks';
import { patientMedicalInformationTranslationKeys } from '../../translations';
import { DescriptionCell } from '../shared/table/DescriptionCell';
import { MedicalInformationModalOpenLink } from '../shared/table/MedicalInformationModalOpenLink';
import { PractitionerTooltipQuestionIcon } from '../shared/table/PractitionerTooltipQuestionIcon';
import { MedicalInformationRemoveLink } from '../shared/table/MedicalInformationRemoveLink';
import { useMedicalInformationRemoveEntryAvailable } from '../shared/useMedicalInformationRemoveEntryAvailable';
import { useShowRemovedMedicalInformationState } from '../MedicalInformationShowRemovedCheckbox.hooks';

import { ChronicDiseaseDetailsModalOpener } from './ChronicDiseaseDetailsModalOpener';

const DEFAULT_ORDERINGS: Ordering<ChronicDisease>[] = [
   {
      descending: true,
      property: 'diagnosisCreationDate',
   },
];

export const useChronicDiseaseInformationDataConfiguration = (
   patientId: number
): LazyLoadedDataConfigurationByCqrsQuery<ChronicDisease> => {
   const [showRemoved] = useShowRemovedMedicalInformationState();

   return useLazyLoadedDataConfigurationByCqrsQuery<ChronicDisease>(() => {
      const query = new ChronicDiseaseQuery();
      query.patientId = patientId;
      query.showRemoved = showRemoved;
      return query;
   }, DEFAULT_ORDERINGS);
};

// eslint-disable-next-line max-lines-per-function
export const useChronicDiseaseInformationTableColumns = (
   onRemoveSuccess: VoidFunction
): TableColumnConfiguration<ChronicDisease>[] => {
   const medicalInformationRemoveEntryAvailable = useMedicalInformationRemoveEntryAvailable();

   // eslint-disable-next-line max-lines-per-function
   return useMemo((): TableColumnConfiguration<ChronicDisease>[] => {
      const columnsWidth = createDynamicColumnsWidth({
         diagnosisCreationDate: '120px',
         diagnosisRemovalDate: '120px',
         diagnosedBy: '25%',
         diagnosis: '31%',
         description: '25%',
         attachments: '19%',
         options: '215px',
      });

      const getBackgroundColor: TableColumnBackgroundColorResolver<ChronicDisease> = (props) =>
         props.bean.medicalInformationRemoved ? colorsMatrix.red.lighter : undefined;

      return [
         {
            accessor: 'diagnosisCreationDate',
            label: patientMedicalInformationTranslationKeys.chronicDisease.table.diagnosisCreationDate,
            width: columnsWidth.diagnosisCreationDate,
            backgroundColor: getBackgroundColor,
            renderer: ({
               bean: { diagnosisCreationDate, createdBy },
            }: ValueRendererProps<ChronicDisease, LocalDate | undefined>) => (
               <StyledFlex gap={'4px'} justifyContent={'space-between'}>
                  {diagnosisCreationDate ? <span>{dateFormatter(diagnosisCreationDate)}</span> : null}
                  {createdBy ? (
                     <PractitionerTooltipQuestionIcon
                        testId={'creationBy'}
                        practitioner={createdBy}
                        label={patientMedicalInformationTranslationKeys.chronicDisease.table.createdBy}
                     />
                  ) : null}
               </StyledFlex>
            ),
         },
         {
            accessor: 'diagnosisRemovalDate',
            label: patientMedicalInformationTranslationKeys.chronicDisease.table.diagnosisRemovalDate,
            width: columnsWidth.diagnosisRemovalDate,
            placeholder: TextPlaceholder.NO_DATA,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean: { diagnosisRemovalDate, removedBy } }: ValueRendererProps<ChronicDisease>) => {
               if (!diagnosisRemovalDate) {
                  return null;
               }

               return (
                  <StyledFlex gap={'4px'} justifyContent={'space-between'}>
                     {diagnosisRemovalDate ? <span>{dateFormatter(diagnosisRemovalDate)}</span> : null}
                     {removedBy ? (
                        <PractitionerTooltipQuestionIcon
                           testId={'removalBy'}
                           practitioner={removedBy}
                           label={patientMedicalInformationTranslationKeys.chronicDisease.table.removedBy}
                        />
                     ) : null}
                  </StyledFlex>
               );
            },
         },
         {
            name: 'diagnosedBy',
            accessor: noop,
            label: patientMedicalInformationTranslationKeys.chronicDisease.table.diagnosedBy,
            width: columnsWidth.diagnosedBy,
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean: { diagnosedByName, diagnosedByLicenceNumber } }: ValueRendererProps<ChronicDisease>) => {
               return (
                  <PractitionerSafePresenter
                     testId={'author'}
                     practitioner={{
                        displayName: diagnosedByName,
                        medicalLicenceNumber: diagnosedByLicenceNumber,
                     }}
                     type={'standalone'}
                     withIdentification
                  />
               );
            },
         },
         {
            name: 'diagnosis',
            accessor: noop,
            label: patientMedicalInformationTranslationKeys.chronicDisease.table.diagnosis,
            width: columnsWidth.diagnosis,
            sortingDisabled: true,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean }: ValueRendererProps<ChronicDisease>) => (
               <ConfidentialMask entry={bean}>
                  {() =>
                     bean.diagnosis ? (
                        <Text testId={'diagnosis'}>
                           {`${bean.diagnosis.getCode().asString()} ${bean.diagnosis.getName().asString()}`}
                        </Text>
                     ) : null
                  }
               </ConfidentialMask>
            ),
         },
         {
            name: 'description',
            accessor: noop,
            label: patientMedicalInformationTranslationKeys.chronicDisease.table.description,
            width: columnsWidth.description,
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean }: ValueRendererProps<ChronicDisease>) => (
               <ConfidentialMask entry={bean}>
                  {() =>
                     bean.description ? <DescriptionCell description={bean.description} testId={bean.id} /> : null
                  }
               </ConfidentialMask>
            ),
         },
         {
            name: 'attachments',
            accessor: noop,
            label: patientMedicalInformationTranslationKeys.shared.table.attachments,
            width: columnsWidth.attachments,
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
            backgroundColor: getBackgroundColor,
            renderer: ({ bean }: ValueRendererProps<ChronicDisease>) => (
               <ConfidentialMask entry={bean}>
                  {() =>
                     isEmpty(bean.attachments) ? null : (
                        <ChronicDiseaseDetailsModalOpener chronicDisease={bean}>
                           {({ openModal }) => (
                              <AttachmentsLinks attachments={bean.attachments} onShowMoreAttachmentsClick={openModal} />
                           )}
                        </ChronicDiseaseDetailsModalOpener>
                     )
                  }
               </ConfidentialMask>
            ),
         },
         {
            name: 'options',
            label: '',
            width: columnsWidth.options,
            accessor: noop,
            sortingDisabled: true,
            justifyContent: JustifyContent.END,
            backgroundColor: getBackgroundColor,
            renderer: (props: ValueRendererProps<ChronicDisease>) => {
               const confidentialButton = getConfidentialAccessButton(props.bean, {
                  resourceType: 'MEDICAL_INFORMATION',
                  refetchQuery: ChronicDiseaseQuery,
               });

               if (confidentialButton) {
                  return confidentialButton;
               }

               return (
                  <>
                     <ChronicDiseaseDetailsModalOpener chronicDisease={props.bean}>
                        {({ openModal }) => (
                           <MedicalInformationModalOpenLink
                              onClick={openModal}
                              unsignedAttachmentCount={props.bean.unsignedAttachmentCount}
                              size={LinkSize.NORMAL}
                           />
                        )}
                     </ChronicDiseaseDetailsModalOpener>

                     {medicalInformationRemoveEntryAvailable(
                        props.bean.createdBy,
                        props.bean.medicalInformationRemoved
                     ) ? (
                        <PatientMedicalInformationRemoveModalOpener
                           medicalInformationId={props.bean.id}
                           onRemoveSuccess={onRemoveSuccess}
                        >
                           {({ openModal }) => <MedicalInformationRemoveLink onClick={openModal} />}
                        </PatientMedicalInformationRemoveModalOpener>
                     ) : null}
                  </>
               );
            },
         },
      ];
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [medicalInformationRemoveEntryAvailable]);
};
